import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { ConfirmationModal, MessageModal } from '../components/modals';
import { 
  PasswordChangeModal, 
  SubscriptionModal, 
  UserEditModal, 
  UserCreateModal, 
  AccountActionsMenu 
} from '../components/admin';
import { extractErrorMessage } from '../utils/helpers';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [managingSubscriptionUser, setManagingSubscriptionUser] = useState(null);
  const [userTab, setUserTab] = useState('active'); // 'active', 'inactive', 'expired', 'all' - for users list
  const [searchQuery, setSearchQuery] = useState('');
  
  // Main navigation tabs for admin
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'cemetery', 'plan'
  
  // Cemetery management states (for admin's own concessions)
  const [concessions, setConcessions] = useState([]);
  const [concessionSearchQuery, setConcessionSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sortColumn, setSortColumn] = useState('num_plan');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showConcessionModal, setShowConcessionModal] = useState(false);
  const [editingConcession, setEditingConcession] = useState(null);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [currentConcessionId, setCurrentConcessionId] = useState(null);
  const [userCounts, setUserCounts] = useState({ active: 0, expired: 0, inactive: 0, all: 0 });
  
  // File management states
  const [concessionFiles, setConcessionFiles] = useState([]);
  const [personFiles, setPersonFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [lightboxFile, setLightboxFile] = useState(null);
  const [tempConcessionFiles, setTempConcessionFiles] = useState([]);
  const [tempPersonFiles, setTempPersonFiles] = useState([]);
  
  // Plan state (for admin's own plan)
  const [adminPlan, setAdminPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  
  // PDF generation success modal
  const [showPdfSuccessModal, setShowPdfSuccessModal] = useState(false);
  const [pdfModalInfo, setPdfModalInfo] = useState({ persons: 0, pages: 0 });
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger'
  });
  
  const [messageModal, setMessageModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  
  // PWA Install state
  const { user, logout } = useAuth();
  
  // Define backend URL within component scope
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const apiUrl = `${backendUrl}/api`;
  
  // Desktop Shortcut Download Function
  const downloadShortcut = () => {
    const appUrl = window.location.origin;
    const appName = 'CIMEO';
    
    // Detect OS
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMac = /Mac|iPhone|iPad|iPod/.test(userAgent);
    const isWindows = /Win/.test(userAgent);
    
    let content, filename, mimeType;
    
    if (isMac) {
      // Create .webloc file for Mac
      content = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
    <key>URL</key>
    <string>${appUrl}</string>
</dict>
</plist>`;
      filename = `${appName}.webloc`;
      mimeType = 'application/xml';
    } else {
      // Create .url file for Windows and others
      content = `[InternetShortcut]
URL=${appUrl}
IconIndex=0`;
      filename = `${appName}.url`;
      mimeType = 'text/plain';
    }
    
    // Create blob and download
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log(`Desktop shortcut downloaded: ${filename}`);
  };
  
  // Fetch admin's own concessions
  const fetchAdminConcessions = async () => {
    try {
      const response = await axios.get(`${API}/client/concessions`);
      setConcessions(response.data);
    } catch (error) {
      console.error('Error fetching admin concessions:', error);
    }
  };
  
  // Fetch admin's own plan
  const fetchAdminPlan = async () => {
    setPlanLoading(true);
    try {
      const response = await axios.get(`${API}/client/cemetery-plan`);
      setAdminPlan(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching admin plan:', error);
      }
      setAdminPlan(null);
    } finally {
      setPlanLoading(false);
    }
  };
  
  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'cemetery') {
      fetchAdminConcessions();
    } else if (activeTab === 'plan') {
      fetchAdminPlan();
    }
  }, [activeTab]);
  
  // Concession handlers
  const handleAddConcession = () => {
    setEditingConcession(null);
    setShowConcessionModal(true);
    setTempConcessionFiles([]);
    setConcessionFiles([]);
  };

  const handleEditConcession = async (concession) => {
    setEditingConcession(concession);
    setShowConcessionModal(true);
    setTempConcessionFiles([]);
    await fetchConcessionFiles(concession.id);
  };

  const handleSaveConcession = async (formData) => {
    try {
      if (editingConcession) {
        const response = await axios.put(`${apiUrl}/client/concessions/${editingConcession.id}`, formData);
        setConcessions(concessions.map(c => c.id === editingConcession.id ? response.data : c));
      } else {
        const response = await axios.post(`${apiUrl}/client/concessions`, formData);
        const newConcession = response.data;
        
        if (tempConcessionFiles.length > 0) {
          for (const tempFile of tempConcessionFiles) {
            try {
              const fileFormData = new FormData();
              fileFormData.append('file', tempFile.file);
              await axios.post(`${apiUrl}/client/concessions/${newConcession.id}/files`, fileFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            } catch (fileError) {
              console.error('Error uploading concession file:', fileError);
            }
          }
        }
        
        setConcessions([...concessions, newConcession]);
        setTempConcessionFiles([]);
      }
      setShowConcessionModal(false);
      setEditingConcession(null);
    } catch (error) {
      console.error('Error saving concession:', error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: extractErrorMessage(error, 'Erreur lors de l\'enregistrement'),
        type: 'error'
      });
    }
  };

  const handleDeleteConcession = (concessionId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Supprimer la concession',
      message: 'Voulez-vous vraiment supprimer cette concession et toutes les personnes associées ? Cette action est irréversible.',
      type: 'danger',
      confirmText: 'CONFIRMER',
      onConfirm: async () => {
        try {
          await axios.delete(`${apiUrl}/client/concessions/${concessionId}`);
          setConcessions(concessions.filter(c => c.id !== concessionId));
          setConfirmModal({ ...confirmModal, isOpen: false });
        } catch (error) {
          console.error('Error deleting concession:', error);
          setMessageModal({
            isOpen: true,
            title: 'Erreur',
            message: 'Erreur lors de la suppression',
            type: 'error'
          });
        }
      }
    });
  };

  // Person handlers
  const handleAddPerson = (concession) => {
    setCurrentConcessionId(concession.id);
    setEditingPerson(null);
    setShowPersonModal(true);
    setTempPersonFiles([]);
    setPersonFiles([]);
  };

  const handleEditPerson = async (person, concession) => {
    setCurrentConcessionId(concession.id);
    setEditingPerson(person);
    setShowPersonModal(true);
    setTempPersonFiles([]);
    await fetchPersonFiles(concession.id, person.id);
  };

  const handleSavePerson = async (formData) => {
    try {
      if (editingPerson) {
        const response = await axios.put(`${apiUrl}/client/persons/${editingPerson.id}`, formData);
        setConcessions(concessions.map(c => {
          if (c.id === currentConcessionId) {
            return {
              ...c,
              persons: c.persons.map(p => p.id === editingPerson.id ? response.data : p)
            };
          }
          return c;
        }));
      } else {
        const response = await axios.post(`${apiUrl}/client/concessions/${currentConcessionId}/persons`, formData);
        const newPerson = response.data;
        
        if (tempPersonFiles.length > 0) {
          for (const tempFile of tempPersonFiles) {
            try {
              const fileFormData = new FormData();
              fileFormData.append('file', tempFile.file);
              await axios.post(`${apiUrl}/client/concessions/${currentConcessionId}/persons/${newPerson.id}/files`, fileFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            } catch (fileError) {
              console.error('Error uploading person file:', fileError);
            }
          }
        }
        
        setConcessions(concessions.map(c => {
          if (c.id === currentConcessionId) {
            return {
              ...c,
              persons: [...c.persons, newPerson]
            };
          }
          return c;
        }));
        setTempPersonFiles([]);
      }
      setShowPersonModal(false);
      setEditingPerson(null);
      setCurrentConcessionId(null);
    } catch (error) {
      console.error('Error saving person:', error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: extractErrorMessage(error, 'Erreur lors de l\'enregistrement'),
        type: 'error'
      });
    }
  };

  const handleDeletePerson = (person, concession) => {
    setConfirmModal({
      isOpen: true,
      title: 'Supprimer la personne',
      message: `Voulez-vous vraiment supprimer ${person.nom} ${person.prenom || ''} de cette concession ?`,
      type: 'danger',
      confirmText: 'CONFIRMER',
      onConfirm: async () => {
        try {
          await axios.delete(`${apiUrl}/client/persons/${person.id}`);
          setConcessions(concessions.map(c => {
            if (c.id === concession.id) {
              return {
                ...c,
                persons: c.persons.filter(p => p.id !== person.id)
              };
            }
            return c;
          }));
          setConfirmModal({ ...confirmModal, isOpen: false });
        } catch (error) {
          console.error('Error deleting person:', error);
          setMessageModal({
            isOpen: true,
            title: 'Erreur',
            message: 'Erreur lors de la suppression',
            type: 'error'
          });
        }
      }
    });
  };

  // File management
  const fetchConcessionFiles = async (concessionId) => {
    try {
      const response = await axios.get(`${apiUrl}/client/concessions/${concessionId}/files`);
      setConcessionFiles(response.data);
    } catch (error) {
      console.error('Error fetching concession files:', error);
      setConcessionFiles([]);
    }
  };

  const fetchPersonFiles = async (concessionId, personId) => {
    try {
      const response = await axios.get(`${apiUrl}/client/concessions/${concessionId}/persons/${personId}/files`);
      setPersonFiles(response.data);
    } catch (error) {
      console.error('Error fetching person files:', error);
      setPersonFiles([]);
    }
  };

  const handleDeleteFile = async (fileId, type) => {
    try {
      if (type === 'concession') {
        await axios.delete(`${apiUrl}/client/concession-files/${fileId}`);
        setConcessionFiles(concessionFiles.filter(f => f.id !== fileId));
      } else {
        await axios.delete(`${apiUrl}/client/person-files/${fileId}`);
        setPersonFiles(personFiles.filter(f => f.id !== fileId));
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: 'Erreur lors de la suppression du fichier',
        type: 'error'
      });
    }
  };

  // Sorting and filtering
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Enhanced search with combined first name + last name support
  const matchesSearch = (person, queryWords) => {
    if (!person || queryWords.length === 0) return false;
    
    const nom = (person.nom || '').toLowerCase();
    const prenom = (person.prenom || '').toLowerCase();
    const fullName = `${prenom} ${nom}`.toLowerCase();
    
    // Check if all query words match in any combination
    return queryWords.every(word => 
      fullName.includes(word) || nom.includes(word) || prenom.includes(word)
    );
  };

  const getSortedConcessions = () => {
    let filtered = [...concessions];
    
    if (concessionSearchQuery) {
      const query = concessionSearchQuery.toLowerCase().trim();
      const queryWords = query.split(/\s+/).filter(w => w.length > 0);
      
      filtered = filtered.filter(c => {
        // Search in concession fields
        const matchesConcessionFields = 
          (c.acheteur && c.acheteur.toLowerCase().includes(query)) ||
          (c.num_plan && c.num_plan.toLowerCase().includes(query)) ||
          (c.num_concession && c.num_concession.toLowerCase().includes(query));
        
        // Search in persons with combined name support
        const matchesPersons = c.persons && c.persons.some(p => matchesSearch(p, queryWords));
        
        return matchesConcessionFields || matchesPersons;
      });
    }
    
    if (!sortColumn) return filtered;
    
    return filtered.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      
      if (sortColumn === 'date_achat') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (sortColumn === 'duree_concession') {
        aVal = aVal === 'perpétuelle' ? 999999 : (parseInt(aVal) || 0);
        bVal = bVal === 'perpétuelle' ? 999999 : (parseInt(bVal) || 0);
      } else {
        aVal = aVal ? aVal.toString().toLowerCase() : '';
        bVal = bVal ? bVal.toString().toLowerCase() : '';
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Generate search suggestions
  const generateSuggestions = (query) => {
    if (!query || query.trim().length === 0) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    const queryLower = query.toLowerCase().trim();
    const queryWords = queryLower.split(/\s+/).filter(w => w.length > 0);
    const suggestions = [];
    const seenNames = new Set();
    
    // Collect all unique persons from all concessions
    concessions.forEach(concession => {
      if (concession.persons) {
        concession.persons.forEach(person => {
          if (matchesSearch(person, queryWords)) {
            const fullName = `${person.prenom || ''} ${person.nom || ''}`.trim();
            const key = fullName.toLowerCase();
            
            if (!seenNames.has(key) && fullName) {
              seenNames.add(key);
              suggestions.push({
                name: fullName,
                person: person,
                concessionId: concession.id,
                concessionInfo: `${concession.num_plan || ''} - ${concession.acheteur || ''}`
              });
            }
          }
        });
      }
      
      // Also match concession buyer name
      if (concession.acheteur && concession.acheteur.toLowerCase().includes(queryLower)) {
        const key = `concession_${concession.id}`;
        if (!seenNames.has(key)) {
          seenNames.add(key);
          suggestions.push({
            name: concession.acheteur,
            concessionId: concession.id,
            concessionInfo: `${concession.num_plan || ''} - Acheteur`,
            isConcession: true
          });
        }
      }
    });
    
    // Sort by relevance (exact matches first, then partial)
    suggestions.sort((a, b) => {
      const aLower = a.name.toLowerCase();
      const bLower = b.name.toLowerCase();
      const aExact = aLower === queryLower;
      const bExact = bLower === queryLower;
      
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      return aLower.localeCompare(bLower);
    });
    
    // Limit to 8 suggestions for performance
    setSearchSuggestions(suggestions.slice(0, 8));
    setShowSuggestions(suggestions.length > 0);
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setConcessionSearchQuery(value);
    generateSuggestions(value);
  };
  
  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setConcessionSearchQuery(suggestion.name);
    setShowSuggestions(false);
    // Optionally expand the concession row
    if (suggestion.concessionId) {
      const newExpanded = new Set(expandedRows);
      newExpanded.add(suggestion.concessionId);
      setExpandedRows(newExpanded);
    }
  };

  const toggleRow = (concessionId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(concessionId)) {
      newExpanded.delete(concessionId);
    } else {
      newExpanded.add(concessionId);
    }
    setExpandedRows(newExpanded);
  };
  
  // Filter users based on search query
  const filteredUsers = users.filter(userItem => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      userItem.email?.toLowerCase().includes(query) ||
      userItem.mayor_name?.toLowerCase().includes(query) ||
      userItem.phone_number?.toLowerCase().includes(query) ||
      userItem.city_hall_address?.toLowerCase().includes(query) ||
      userItem.role?.toLowerCase().includes(query)
    );
  });
  
  // Handle account actions
  const handleAccountAction = async (action, userItem) => {
    console.log('handleAccountAction called:', action, userItem.email);
    const token = localStorage.getItem('access_token');
    
    if (!token) {
      setMessageModal({
        isOpen: true,
        title: 'Authentification requise',
        message: 'Token d\'authentification manquant. Veuillez vous reconnecter.',
        type: 'error'
      });
      return;
    }
    
    try {
      switch (action) {
        case 'edit':
          console.log('Opening edit modal for:', userItem.email);
          setEditingUser(userItem);
          setShowEditUserModal(true);
          break;
          
        case 'subscription':
          console.log('Managing subscription for:', userItem.email);
          setManagingSubscriptionUser(userItem);
          setShowSubscriptionModal(true);
          break;
          
        case 'deactivate':
          console.log('Deactivating user:', userItem.email);
          setConfirmModal({
            isOpen: true,
            title: 'Désactiver le compte',
            message: `Voulez-vous vraiment désactiver ce compte ?\n\nUtilisateur: ${userItem.email}\n\nLe compte sera transféré dans l'onglet "Comptes supprimés".`,
            type: 'danger',
            onConfirm: async () => {
              console.log('Deactivation confirmed, sending DELETE request');
              const response = await fetch(`${apiUrl}/admin/users/${userItem.id}`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              console.log('DELETE response status:', response.status);
              
              if (response.ok) {
                const result = await response.json();
                console.log('Deactivation successful:', result);
                fetchData(); // Refresh the data to update the view
              } else {
                const error = await response.json();
                console.error('Deactivation failed:', error);
                setMessageModal({
                  isOpen: true,
                  title: 'Erreur',
                  message: 'Erreur: ' + (error.detail || 'Erreur inconnue'),
                  type: 'error'
                });
              }
            }
          });
          break;
          
        case 'activate':
          console.log('Reactivating user:', userItem.email);
          setConfirmModal({
            isOpen: true,
            title: 'Réactiver le compte',
            message: `Voulez-vous réactiver ce compte ?\n\nUtilisateur: ${userItem.email}\n\nLe compte sera transféré dans l'onglet "Comptes actifs".`,
            type: 'success',
            confirmText: 'Réactiver',
            onConfirm: async () => {
              console.log('Reactivation confirmed, sending PUT request');
              const response = await fetch(`${apiUrl}/admin/users/${userItem.id}/reactivate`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              console.log('PUT response status:', response.status);
              
              if (response.ok) {
                const result = await response.json();
                console.log('Reactivation successful:', result);
                fetchData(); // Refresh the data to update the view
              } else {
                const error = await response.json();
                console.error('Reactivation failed:', error);
                setMessageModal({
                  isOpen: true,
                  title: 'Erreur',
                  message: 'Erreur: ' + (error.detail || 'Erreur inconnue'),
                  type: 'error'
                });
              }
            }
          });
          break;
          
        case 'delete_permanent':
          console.log('Permanently deleting user:', userItem.email);
          setConfirmModal({
            isOpen: true,
            title: 'Supprimer définitivement le compte',
            message: `⚠️ ATTENTION : Cette action est irréversible !\n\nVoulez-vous vraiment supprimer définitivement ce compte ?\n\nUtilisateur: ${userItem.email}\n\nToutes les données associées seront supprimées :\n• Concessions\n• Personnes inhumées\n• Fichiers et documents\n• Plans de cimetière`,
            type: 'danger',
            confirmText: 'Supprimer définitivement',
            onConfirm: async () => {
              console.log('Permanent deletion confirmed, sending DELETE request');
              const response = await fetch(`${apiUrl}/admin/users/${userItem.id}/permanent`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              console.log('DELETE permanent response status:', response.status);
              
              if (response.ok) {
                const result = await response.json();
                console.log('Permanent deletion successful:', result);
                setMessageModal({
                  isOpen: true,
                  title: 'Compte supprimé',
                  message: `Le compte ${userItem.email} et toutes ses données ont été supprimés définitivement.`,
                  type: 'success'
                });
                fetchData(); // Refresh the data to update the view
              } else {
                const error = await response.json();
                console.error('Permanent deletion failed:', error);
                setMessageModal({
                  isOpen: true,
                  title: 'Erreur',
                  message: 'Erreur: ' + (error.detail || 'Erreur inconnue'),
                  type: 'error'
                });
              }
            }
          });
          break;
          
        default:
          console.error('Unknown action:', action);
          setMessageModal({
            isOpen: true,
            title: 'Erreur',
            message: 'Action inconnue: ' + action,
            type: 'error'
          });
      }
    } catch (error) {
      console.error('Error handling action:', action, error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur technique',
        message: 'Erreur technique: ' + error.message,
        type: 'error'
      });
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Map userTab to backend filter_type
      const filterMap = {
        'active': 'active',
        'inactive': 'inactive',
        'expired': 'expired',
        'all': 'all'
      };
      const filterType = filterMap[userTab] || 'active';
      
      const [usersResponse, statsResponse, countsResponse] = await Promise.all([
        axios.get(`${API}/admin/users?filter_type=${filterType}`),
        axios.get(`${API}/admin/dashboard-stats`),
        axios.get(`${API}/admin/users/counts`)
      ]);
      
      setUsers(usersResponse.data);
      setStats(statsResponse.data);
      setUserCounts(countsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchData();
    }
  }, [activeTab, userTab]);

  const handleUserCreated = (userData) => {
    setMessageModal({
      isOpen: true,
      title: 'Utilisateur créé',
      message: `Utilisateur créé avec succès!\n\nEmail: ${userData.email}\nCode d&apos;accès: ${userData.access_code}\n\nLe client peut maintenant se connecter avec ce code.`,
      type: 'success'
    });
    fetchData();
  };

  const handleUserUpdated = (userData) => {
    setMessageModal({
      isOpen: true,
      title: 'Utilisateur modifié',
      message: `Utilisateur modifié avec succès!\n\nEmail: ${userData.email}`,
      type: 'success'
    });
    fetchData();
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setShowEditUserModal(true);
  };

  const handleCreateSubscription = async (userId) => {
    const expiryDate = prompt('Date d\'expiration (YYYY-MM-DD):');
    if (expiryDate) {
      try {
        await axios.post(`${API}/admin/subscriptions`, {
          user_id: userId,
          expiry_date: new Date(expiryDate).toISOString()
        });
        setMessageModal({
          isOpen: true,
          title: 'Succès',
          message: 'Abonnement créé avec succès!',
          type: 'success'
        });
        fetchData();
      } catch (error) {
        setMessageModal({
          isOpen: true,
          title: 'Erreur',
          message: extractErrorMessage(error, 'Une erreur est survenue'),
          type: 'error'
        });
      }
    }
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#1A1E2E'}}>
      {/* Header */}
      <div className="shadow-lg" style={{backgroundColor: '#1C2133'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              {/* CIEMO Logo avec effet néon */}
              <img 
                src="/cimeo-logo.png" 
                alt="CIMÉO" 
                style={{
                  height: '42px',
                  width: 'auto',
                  opacity: 0.95,
                  filter: 'drop-shadow(0 4px 12px rgba(0, 194, 255, 0.4)) drop-shadow(0 0 8px rgba(0, 194, 255, 0.3))',
                  transition: 'filter 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'drop-shadow(0 6px 16px rgba(0, 194, 255, 0.6)) drop-shadow(0 0 12px rgba(0, 194, 255, 0.5))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'drop-shadow(0 4px 12px rgba(0, 194, 255, 0.4)) drop-shadow(0 0 8px rgba(0, 194, 255, 0.3))';
                }}
              />
              <div>
                <h1 className="text-2xl font-bold" style={{color: '#FFFFFF'}}>Dashboard Administrateur</h1>
                <p className="text-sm" style={{color: '#8A92B2'}}>Gestion des utilisateurs et abonnements</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {user?.is_first_login === true && (
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="px-4 py-2 rounded-md transition-all duration-300"
                  style={{
                    backgroundColor: '#FFC107',
                    color: '#1A1E2E'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 193, 7, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  Changer mot de passe
                </button>
              )}
              <span className="text-sm" style={{color: '#8A92B2'}}>Admin: {user?.email}</span>
              
              {/* Desktop Shortcut Button - Downloads shortcut file directly */}
              <button
                onClick={downloadShortcut}
                className="px-4 py-2 rounded-md transition-all duration-300"
                style={{
                  backgroundColor: 'rgba(0, 194, 255, 0.15)',
                  color: '#00C2FF',
                  border: '1px solid rgba(0, 194, 255, 0.3)',
                  fontSize: '13px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.25)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.15)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                title="Télécharger le raccourci bureau"
              >
                📱 Raccourci bureau
              </button>
              
              <button
                onClick={logout}
                className="px-4 py-2 rounded-md transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
                  color: '#FFFFFF'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* First login warning */}
        {user?.is_first_login === true && (
          <div className="px-4 py-3 rounded mb-6" style={{
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid #FFC107',
            color: '#FFC107'
          }}>
            <strong>Attention:</strong> Vous utilisez le mot de passe par défaut. Changez-le immédiatement pour sécuriser votre compte.
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 border-b" style={{borderColor: '#3A4359'}}>
          <button
            onClick={() => setActiveTab('users')}
            className="px-4 py-3 font-medium transition-all duration-300"
            style={{
              color: activeTab === 'users' ? '#00C2FF' : '#8A92B2',
              borderBottom: activeTab === 'users' ? '3px solid #00C2FF' : '3px solid transparent'
            }}
          >
            Gestion des utilisateurs
          </button>
        </div>

        {/* Statistics Cards - Only show in users tab */}
        {activeTab === 'users' && (
        <div>
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="rounded-lg shadow-lg p-6" style={{backgroundColor: '#2C3148'}}>
            <div className="flex items-center">
              <div className="p-3 rounded-full" style={{backgroundColor: 'rgba(74, 91, 255, 0.2)'}}>
                <span className="text-2xl">👥</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium" style={{color: '#8A92B2'}}>Total Utilisateurs</p>
                <p className="text-2xl font-bold" style={{color: '#FFFFFF'}}>{stats.total_users || 0}</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg shadow-lg p-6" style={{backgroundColor: '#2C3148'}}>
            <div className="flex items-center">
              <div className="p-3 rounded-full" style={{backgroundColor: 'rgba(0, 194, 255, 0.2)'}}>
                <span className="text-2xl">✅</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium" style={{color: '#8A92B2'}}>Abonnements Actifs</p>
                <p className="text-2xl font-bold" style={{color: '#00C2FF'}}>{stats.active_subscriptions || 0}</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg shadow-lg p-6" style={{backgroundColor: '#2C3148'}}>
            <div className="flex items-center">
              <div className="p-3 rounded-full" style={{backgroundColor: 'rgba(255, 87, 87, 0.2)'}}>
                <span className="text-2xl">❌</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium" style={{color: '#8A92B2'}}>Abonnements Expirés</p>
                <p className="text-2xl font-bold" style={{color: '#FF5757'}}>{stats.expired_subscriptions || 0}</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg shadow-lg p-6" style={{backgroundColor: '#2C3148'}}>
            <div className="flex items-center">
              <div className="p-3 rounded-full" style={{backgroundColor: 'rgba(255, 193, 7, 0.2)'}}>
                <span className="text-2xl">🔑</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium" style={{color: '#8A92B2'}}>Première Connexion</p>
                <p className="text-2xl font-bold" style={{color: '#FFC107'}}>{stats.pending_first_login || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* User Management */}
        <div className="rounded-lg shadow-lg" style={{backgroundColor: '#2C3148'}}>
          <div className="px-6 py-4 border-b flex justify-between items-center" style={{borderColor: '#1C2133'}}>
            <h2 className="text-lg font-medium" style={{color: '#FFFFFF'}}>Gestion des Utilisateurs</h2>
            <button
              onClick={() => setShowCreateUserModal(true)}
              className="px-4 py-2 rounded-md transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
                color: '#FFFFFF'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              Nouvel utilisateur
            </button>
          </div>

          {/* Tabs with counters and Search Bar */}
          <div className="border-b" style={{borderColor: '#1C2133'}}>
            <div className="px-6 flex items-center justify-between">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setUserTab('active')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                    userTab === 'active'
                      ? ''
                      : 'border-transparent'
                  }`}
                  style={{
                    borderBottomColor: userTab === 'active' ? '#00C2FF' : 'transparent',
                    color: userTab === 'active' ? '#00C2FF' : '#8A92B2'
                  }}
                  onMouseEnter={(e) => {
                    if (userTab !== 'active') e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    if (userTab !== 'active') e.currentTarget.style.color = '#8A92B2';
                  }}
                >
                  <span>Comptes actifs</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}
                    style={{
                      backgroundColor: userTab === 'active' ? 'rgba(0, 194, 255, 0.2)' : 'rgba(91, 99, 140, 0.2)',
                      color: userTab === 'active' ? '#00C2FF' : '#8A92B2'
                    }}>
                    {userCounts.active}
                  </span>
                </button>
                <button
                  onClick={() => setUserTab('expired')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                    userTab === 'expired'
                      ? ''
                      : 'border-transparent'
                  }`}
                  style={{
                    borderBottomColor: userTab === 'expired' ? '#FFC107' : 'transparent',
                    color: userTab === 'expired' ? '#FFC107' : '#8A92B2'
                  }}
                  onMouseEnter={(e) => {
                    if (userTab !== 'expired') e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    if (userTab !== 'expired') e.currentTarget.style.color = '#8A92B2';
                  }}
                >
                  <span>Compte échéance</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}
                    style={{
                      backgroundColor: userTab === 'expired' ? 'rgba(255, 193, 7, 0.2)' : 'rgba(91, 99, 140, 0.2)',
                      color: userTab === 'expired' ? '#FFC107' : '#8A92B2'
                    }}>
                    {userCounts.expired}
                  </span>
                </button>
                <button
                  onClick={() => setUserTab('inactive')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                    userTab === 'inactive'
                      ? ''
                      : 'border-transparent'
                  }`}
                  style={{
                    borderBottomColor: userTab === 'inactive' ? '#FF5757' : 'transparent',
                    color: userTab === 'inactive' ? '#FF5757' : '#8A92B2'
                  }}
                  onMouseEnter={(e) => {
                    if (userTab !== 'inactive') e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    if (userTab !== 'inactive') e.currentTarget.style.color = '#8A92B2';
                  }}
                >
                  <span>Comptes supprimés</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}
                    style={{
                      backgroundColor: userTab === 'inactive' ? 'rgba(255, 87, 87, 0.2)' : 'rgba(91, 99, 140, 0.2)',
                      color: userTab === 'inactive' ? '#FF5757' : '#8A92B2'
                    }}>
                    {userCounts.inactive}
                  </span>
                </button>
                <button
                  onClick={() => setUserTab('all')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                    userTab === 'all'
                      ? ''
                      : 'border-transparent'
                  }`}
                  style={{
                    borderBottomColor: userTab === 'all' ? '#4A5BFF' : 'transparent',
                    color: userTab === 'all' ? '#4A5BFF' : '#8A92B2'
                  }}
                  onMouseEnter={(e) => {
                    if (userTab !== 'all') e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    if (userTab !== 'all') e.currentTarget.style.color = '#8A92B2';
                  }}
                >
                  <span>Tous les comptes</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}
                    style={{
                      backgroundColor: userTab === 'all' ? 'rgba(74, 91, 255, 0.2)' : 'rgba(91, 99, 140, 0.2)',
                      color: userTab === 'all' ? '#4A5BFF' : '#8A92B2'
                    }}>
                    {userCounts.all}
                  </span>
                </button>
              </nav>
              
              {/* Search Bar - aligned to right */}
              <div className="flex items-center space-x-2">
                <div className="relative w-64">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4" style={{color: '#8A92B2'}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-9 pr-8 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-1"
                    style={{
                      backgroundColor: '#242A3D',
                      borderColor: '#4A5BFF',
                      color: '#FFFFFF'
                    }}
                    placeholder="Rechercher…"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-2 flex items-center"
                    >
                      <svg className="h-4 w-4 transition-colors duration-200" style={{color: '#8A92B2'}} 
                        onMouseEnter={(e) => e.currentTarget.style.color = '#00C2FF'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#8A92B2'}
                        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <span className="text-xs text-gray-600 whitespace-nowrap">
                    {filteredUsers.length} résultat{filteredUsers.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <div style={{color: '#8A92B2'}}>Chargement...</div>
              </div>
            ) : (
              <table className="min-w-full divide-y" style={{divideColor: '#1C2133'}}>
                <thead style={{backgroundColor: '#1C2133'}}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#8A92B2'}}>
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#8A92B2'}}>
                      Informations
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#8A92B2'}}>
                      Rôle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#8A92B2'}}>
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#8A92B2'}}>
                      Abonnement
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#8A92B2'}}>
                      Gestion
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{backgroundColor: '#2C3148', divideColor: '#1C2133'}}>
                  {filteredUsers.map((userItem) => (
                    <tr 
                      key={userItem.id} 
                      onClick={() => handleAccountAction('edit', userItem)}
                      className="cursor-pointer transition-colors duration-150"
                      style={{backgroundColor: '#2C3148'}}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#242A3D'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2C3148'}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium" style={{color: '#FFFFFF'}}>{userItem.email}</div>
                          <div className="text-sm" style={{color: '#8A92B2'}}>Créé: {new Date(userItem.created_at).toLocaleDateString()}</div>
                          {userItem.access_code && (
                            <div className="text-xs" style={{color: '#00C2FF'}}>Code: {userItem.access_code}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          {userItem.city_hall_address && (
                            <div style={{color: '#FFFFFF'}}>🏛️ {userItem.city_hall_address}</div>
                          )}
                          {userItem.mayor_name && (
                            <div style={{color: '#8A92B2'}}>👤 {userItem.mayor_name}</div>
                          )}
                          {userItem.phone_number && (
                            <div style={{color: '#8A92B2'}}>📞 {userItem.phone_number}</div>
                          )}
                          {!userItem.city_hall_address && !userItem.mayor_name && !userItem.phone_number && (
                            <span style={{color: '#5B638C'}}>Aucune info</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}
                          style={{
                            backgroundColor: userItem.role === 'admin' 
                              ? 'rgba(122, 95, 255, 0.2)' 
                              : userItem.role === 'demo'
                                ? 'rgba(255, 184, 0, 0.2)'
                                : 'rgba(74, 91, 255, 0.2)',
                            color: userItem.role === 'admin' 
                              ? '#7A5FFF' 
                              : userItem.role === 'demo'
                                ? '#FFB800'
                                : '#4A5BFF'
                          }}>
                          {userItem.role === 'demo' ? 'Demo' : userItem.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {userTab === 'inactive' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: 'rgba(255, 87, 87, 0.2)',
                              color: '#FF5757'
                            }}>
                            Désactivé
                          </span>
                        ) : userItem.is_first_login ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: 'rgba(255, 193, 7, 0.2)',
                              color: '#FFC107'
                            }}>
                            Première connexion
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: 'rgba(0, 194, 255, 0.2)',
                              color: '#00C2FF'
                            }}>
                            Actif
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {/* Check account status first, then subscription */}
                        {!userItem.is_active ? (
                          // Account is suspended
                          <div>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: 'rgba(255, 87, 87, 0.2)',
                                color: '#FF5757'
                              }}>
                              Suspendu
                            </span>
                          </div>
                        ) : userItem.subscription_expiry ? (
                          // Account is active with subscription
                          <div>
                            <div className="text-xs mb-1" style={{color: '#8A92B2'}}>
                              Expire: {new Date(userItem.subscription_expiry).toLocaleDateString('fr-FR')}
                            </div>
                            <div>
                              {userItem.subscription_active && !userItem.subscription_expired ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                  style={{
                                    backgroundColor: 'rgba(0, 194, 255, 0.2)',
                                    color: '#00C2FF'
                                  }}>
                                  Actif
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                  style={{
                                    backgroundColor: 'rgba(255, 193, 7, 0.2)',
                                    color: '#FFC107'
                                  }}>
                                  Abonnement expiré
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          // No subscription
                          <span className="text-gray-400">Aucun</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        <AccountActionsMenu 
                          userItem={userItem}
                          userTab={userTab}
                          onAction={handleAccountAction}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            
            {users.length === 0 && !isLoading && (
              <div className="text-center py-8" style={{color: '#8A92B2'}}>
                Aucun utilisateur trouvé
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      {/* PDF Success Modal */}
      {showPdfSuccessModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowPdfSuccessModal(false)}
        >
          <div 
            style={{
              backgroundColor: '#1C2133',
              border: '2px solid #00C2FF',
              borderRadius: '12px',
              padding: '30px 40px',
              maxWidth: '500px',
              boxShadow: '0 20px 60px rgba(0, 194, 255, 0.3)',
              animation: 'slideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
              <h3 style={{ 
                color: '#00C2FF', 
                fontSize: '24px', 
                fontWeight: 'bold',
                marginBottom: '15px'
              }}>
                PDF généré avec succès !
              </h3>
              <div style={{ 
                color: '#8A92B2', 
                fontSize: '16px',
                marginBottom: '10px' 
              }}>
                <span style={{ color: '#00FFB3', fontWeight: 'bold' }}>{pdfModalInfo.persons}</span> personne(s) imprimée(s)
              </div>
              <div style={{ 
                color: '#8A92B2', 
                fontSize: '16px',
                marginBottom: '25px' 
              }}>
                <span style={{ color: '#00C2FF', fontWeight: 'bold' }}>{pdfModalInfo.pages}</span> page(s) générée(s)
              </div>
              <button
                onClick={() => setShowPdfSuccessModal(false)}
                style={{
                  background: 'linear-gradient(135deg, #00C2FF 0%, #0099CC 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 194, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      
      </div>

      <PasswordChangeModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSuccess={() => {
          setMessageModal({
            isOpen: true,
            title: 'Succès',
            message: 'Mot de passe modifié avec succès!',
            type: 'success'
          });
        }}
      />
      
      <UserCreateModal
        isOpen={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        onUserCreated={handleUserCreated}
      />
      
      <UserEditModal
        isOpen={showEditUserModal}
        onClose={() => {
          setShowEditUserModal(false);
          setEditingUser(null);
        }}
        onUserUpdated={handleUserUpdated}
        editingUser={editingUser}
        setMessageModal={setMessageModal}
      />
      
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          setManagingSubscriptionUser(null);
        }}
        onSuccess={fetchData}
        userItem={managingSubscriptionUser}
        setMessageModal={setMessageModal}
      />
      
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
      />
      
      <MessageModal
        isOpen={messageModal.isOpen}
        onClose={() => setMessageModal({ ...messageModal, isOpen: false })}
        title={messageModal.title}
        message={messageModal.message}
        type={messageModal.type}
      />
    </div>
  );
};

export default AdminDashboard;
